import { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '@components/layout/page-header';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DateInput from '@components/atoms/ui-kit/date-input.tsx';
import { useDebouncedValue } from '@utils/hooks/use-debounced-value';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';

import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';

import { PATHS } from '../../router/paths';
import { useGetUsageBo } from '@utils/api/back-office/usage/use-get-usage-bo';

const formatDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const getDefaultDates = () => {
  const currentDate = new Date();
  const firstDayCurrentMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1
  );
  return {
    period_start: formatDate(firstDayCurrentMonth),
    period_end: formatDate(currentDate)
  };
};

type Row = {
  id: number;
  organization_id: number;
  organization_name: string;
  stt_units: string;
  alignment_units: string;
  translation_characters: string | number;
  auto_minute_exports: string | number;
  custom_models_hosted: string | number;
  custom_models_iterations: string | number;
  total_cost: string;
};

export default function UsageBo() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const defaultDates = getDefaultDates();

  const { searchParams, setSearchParams } = useNewAppSearchParams();

  useEffect(() => {
    const limit = searchParams.limit || '25';
    setSearchParams({
      offset: '0',
      limit,
      q: searchParams.q || undefined,
      sort: searchParams.sort || undefined,
      direction: searchParams.direction || undefined
    });
  }, []);

  const [params, setParams] = useState({
    period_start: defaultDates.period_start,
    period_end: defaultDates.period_end
  });

  // Debounce the date params for the query
  const debouncedQueryParams = useDebouncedValue(
    { period_start: params.period_start, period_end: params.period_end },
    500
  );

  const { data, isLoading, isError } = useGetUsageBo(debouncedQueryParams);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        header: t('id'),
        field: 'organization_id',
        maxWidth: 90,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('name'),
        field: 'organization_name',
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('total-cost'),
        field: 'total_cost',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('translated-chars'),
        field: 'translation_characters',
        minWidth: 180,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('stt-units'),
        field: 'stt_units',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('exports'),
        field: 'auto_minute_exports',
        minWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('models-hosted'),
        field: 'custom_models_hosted',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('model-iterations'),
        field: 'custom_models_iterations',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      },
      {
        header: t('alignment-minutes'),
        field: 'alignment_units',
        minWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        cell: ({ getValue }) => String(getValue() ?? '')
      }
    ];
  }, [t]);

  const handleCellClick = useCallback(
    (row: Row, field: string) => {
      if (field === 'organization_id' || field === 'organization_name') {
        navigate(PATHS(String(row.organization_id)).CUSTOMER_CONTENT);
      }
    },
    [navigate]
  );

  const todayStr = formatDate(new Date());

  const handleDateChange = (
    field: 'period_start' | 'period_end',
    value: string
  ) => {
    setParams(prev => {
      const next = { ...prev, [field]: value };
      if (
        field === 'period_start' &&
        value &&
        next.period_end &&
        value > next.period_end
      ) {
        next.period_end = value;
      }
      if (
        field === 'period_end' &&
        value &&
        next.period_start &&
        value < next.period_start
      ) {
        next.period_start = value;
      }
      return next;
    });

    // Reset to first page but keep other server-side params intact
    const limit = searchParams.limit || '25';
    setSearchParams({
      offset: '0',
      limit,
      q: searchParams.q || undefined,
      sort: searchParams.sort || undefined,
      direction: searchParams.direction || undefined
    });
  };

  return (
    <div className="bg-app flex-1">
      <PageHeader
        title={t('usage-activity-reports')}
        caption={t('view-and-update-usage-below')}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
          <DateInput
            id="period_start"
            label={t('start-date')}
            value={params.period_start}
            onChange={v => handleDateChange('period_start', v)}
            min="2000-01-01"
            max={params.period_end || todayStr}
            withShortcuts
            size="small"
          />
          <DateInput
            id="period_end"
            label={t('end-date')}
            value={params.period_end}
            onChange={v => handleDateChange('period_end', v)}
            min={params.period_start || '2000-01-01'}
            max={todayStr}
            withShortcuts
            size="small"
          />
        </div>
      </PageHeader>

      <div className="w-full">
        {isError && (
          <p className="px-4 py-2 text-sm text-red-600">
            {t('an-unexpected-error-occurred-please-try-again')}
          </p>
        )}
        <DataGridV2<Row>
          data={data?.rows ?? []}
          columns={columns}
          enableRowSelection={false}
          enablePagination
          manualPagination
          enableSorting
          manualSorting
          enableFiltering={false}
          manualFiltering={false}
          totalItems={data?.total ?? 0}
          pageSize={25}
          pageSizeOptions={[25, 50, 75, 100]}
          showColumnFilters={false}
          filterMode="popover"
          height="84vh"
          dense
          grid={false}
          striped
          loading={isLoading}
          getRowId={row => String(row.id)}
          onCellClick={handleCellClick}
        />
      </div>
    </div>
  );
}
